// Generated by scripts/sync-karen-visual-plan.mjs from ../deliverables/karen_visual_preview/karen-pages.js.
// Do not hand-edit screen inventory here; update the Karen visual plan and rerun the sync script.

export type WeHonorPlanGroup = "family" | "staff" | "paperwork" | "memorial" | "office";

export interface WeHonorPlanPage {
  id: string;
  section: string;
  group: WeHonorPlanGroup;
  title: string;
  audience: string;
  purpose: string;
  sees: readonly string[];
  actions: readonly string[];
  problemSolved: string;
  layout: {
    header?: string;
    leftNav?: readonly string[];
    cards?: readonly string[];
    formGroups?: readonly string[];
    mainSections?: readonly string[];
    statusChips?: readonly string[];
    tableHeaders?: readonly string[];
    rows?: readonly (readonly string[])[];
    notes?: readonly string[];
    actions?: readonly string[];
  };
  route: string;
  wireframe: string;
  sequence: number;
  acceptanceCriteria: readonly string[];
}

export interface WeHonorModuleAcceptance {
  id: string;
  title: string;
  criteria: readonly string[];
}

export const weHonorSystemPlan = {
  "title": "WeHonor for Monarch",
  "subtitle": "A Visual Walkthrough of the Planned Case, Arrangement, Paperwork, Website, and Family Management System",
  "shortSubtitle": "A planned system for online arrangements, case management, paperwork, signatures, contracts, memorial pages, guestbook, and family follow-up.",
  "flow": [
    "Monarch Website",
    "Family Starts Arrangement",
    "Vital Statistics and Selections",
    "Staff Reviews Case",
    "Forms and Signatures",
    "Contract and Financials",
    "Memorial Page, Guestbook, Service Folder",
    "Follow-Up and Records"
  ],
  "pages": [
    {
      "id": "monarch-website-start",
      "section": "A. Monarch Website and Family Start",
      "group": "family",
      "title": "Monarch Website Start Page",
      "audience": "Families visiting the Monarch website",
      "purpose": "Gives families a clear place to begin arrangements online or contact Monarch for help.",
      "sees": [
        "Monarch logo and contact information",
        "Start Arrangements Online",
        "Pre-Plan for the Future",
        "Continue Existing Arrangement",
        "Call Monarch"
      ],
      "actions": [
        "Start arrangements",
        "Pre-plan",
        "Call Monarch",
        "Continue a saved arrangement"
      ],
      "problemSolved": "Families do not need a separate link or confusing third-party page to begin.",
      "layout": {
        "header": "Monarch Website",
        "leftNav": [
          "Home",
          "Services",
          "Planning",
          "Contact"
        ],
        "cards": [
          "Immediate Need",
          "Pre-Planning",
          "Continue Arrangement"
        ],
        "mainSections": [
          "Trust message",
          "Care team contact",
          "Online arrangement button"
        ],
        "actions": [
          "Start Arrangements Online",
          "Call Monarch"
        ]
      },
      "route": "/arrangements/start",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/monarch-website-start.png",
      "sequence": 1,
      "acceptanceCriteria": [
        "Monarch Website Start Page renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Monarch logo and contact information.",
        "The primary action \"Start arrangements\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "arrangement-type-selection",
      "section": "A. Monarch Website and Family Start",
      "group": "family",
      "title": "Arrangement Type Selection",
      "audience": "Families and staff helping a family by phone",
      "purpose": "Helps the family choose the right starting point.",
      "sees": [
        "A loved one has passed",
        "A death is expected soon",
        "I want to pre-plan",
        "I already spoke with Monarch"
      ],
      "actions": [
        "Choose arrangement type",
        "Enter access code",
        "Call Monarch"
      ],
      "problemSolved": "Staff can see why a family started and what kind of help they need.",
      "layout": {
        "header": "How can Monarch help you today?",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Immediate Need",
          "Expected Soon",
          "Pre-Planning",
          "Already Working With Monarch"
        ],
        "mainSections": [
          "Private access code",
          "Care team phone number"
        ],
        "actions": [
          "Continue",
          "Call Monarch"
        ]
      },
      "route": "/arrangements/studio",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/arrangement-type-selection.png",
      "sequence": 2,
      "acceptanceCriteria": [
        "Arrangement Type Selection renders with the Monarch-branded shell and screen title visible.",
        "The screen shows A loved one has passed.",
        "The primary action \"Choose arrangement type\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "secure-family-access",
      "section": "A. Monarch Website and Family Start",
      "group": "family",
      "title": "Secure Family Access",
      "audience": "Families returning to a saved arrangement",
      "purpose": "Lets a family safely return to their arrangement or open a staff-sent link.",
      "sees": [
        "Email or phone verification",
        "One-click secure access link",
        "Resume saved arrangement",
        "Privacy reassurance"
      ],
      "actions": [
        "Send access link",
        "Verify code",
        "Resume arrangement",
        "Call Monarch"
      ],
      "problemSolved": "Families can come back later without starting over.",
      "layout": {
        "header": "Return to Your Arrangement",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Email Verification",
          "Phone Verification",
          "Saved Arrangement"
        ],
        "formGroups": [
          "Email or phone",
          "Verification code"
        ],
        "mainSections": [
          "Privacy note",
          "Need help panel"
        ],
        "actions": [
          "Send Link",
          "Verify",
          "Resume"
        ]
      },
      "route": "/family/portal/monarch-family",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/secure-family-access.png",
      "sequence": 3,
      "acceptanceCriteria": [
        "Secure Family Access renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Email or phone verification.",
        "The primary action \"Send access link\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "arrangement-welcome",
      "section": "A. Monarch Website and Family Start",
      "group": "family",
      "title": "Arrangement Welcome",
      "audience": "Families beginning online arrangements",
      "purpose": "Explains what the family will complete and what Monarch will review.",
      "sees": [
        "Simple progress overview",
        "Estimated time",
        "Monarch review message",
        "Save and return note"
      ],
      "actions": [
        "Begin",
        "Save for later",
        "Call Monarch"
      ],
      "problemSolved": "Families understand that their answers help staff prepare, but nothing is final until reviewed.",
      "layout": {
        "header": "Welcome to Monarch Online Arrangements",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "1. Family Details",
          "2. Loved One Details",
          "3. Service Choices",
          "4. Review"
        ],
        "mainSections": [
          "What happens next",
          "What Monarch will review"
        ],
        "actions": [
          "Begin Arrangement",
          "Save and Exit"
        ]
      },
      "route": "/arrangements/studio",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/arrangement-welcome.png",
      "sequence": 4,
      "acceptanceCriteria": [
        "Arrangement Welcome renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Simple progress overview.",
        "The primary action \"Begin\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "planner-informant-information",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Planner / Family Representative Information",
      "audience": "Family member, primary contact, or staff helping by phone",
      "purpose": "Collects the person Monarch should contact about the case.",
      "sees": [
        "Name",
        "Relationship",
        "Phone",
        "Email",
        "Address",
        "Preferred contact method",
        "Permission to receive updates"
      ],
      "actions": [
        "Save and continue",
        "Add another family contact",
        "Ask staff for help"
      ],
      "problemSolved": "Karen has one clear family contact instead of searching through texts and emails.",
      "layout": {
        "header": "Family Representative",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Name and relationship",
          "Phone and email",
          "Address",
          "Preferred contact method"
        ],
        "notes": [
          "Permission to receive updates"
        ],
        "actions": [
          "Save and Continue",
          "Add Contact"
        ]
      },
      "route": "/workspace/planner-informant-information",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/planner-informant-information.png",
      "sequence": 5,
      "acceptanceCriteria": [
        "Planner / Family Representative Information renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Name.",
        "The primary action \"Save and continue\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "loved-one-basic-information",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Loved One Basic Information",
      "audience": "Family member or staff",
      "purpose": "Captures the first case details Monarch needs.",
      "sees": [
        "Full legal name",
        "Preferred name",
        "Date of passing",
        "Place of passing",
        "Current location",
        "Ready for release status"
      ],
      "actions": [
        "Save and continue",
        "Mark unknown",
        "Request Monarch help"
      ],
      "problemSolved": "The case starts with the right loved one details and release status.",
      "layout": {
        "header": "Loved One Details",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Legal name",
          "Preferred name",
          "Date and place of passing",
          "Current location"
        ],
        "cards": [
          "Release readiness"
        ],
        "actions": [
          "Save and Continue",
          "Request Help"
        ]
      },
      "route": "/workspace/loved-one-basic-information",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/loved-one-basic-information.png",
      "sequence": 6,
      "acceptanceCriteria": [
        "Loved One Basic Information renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Full legal name.",
        "The primary action \"Save and continue\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-statistics-overview",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics Wizard Overview",
      "audience": "Family representative and staff",
      "purpose": "Shows the death certificate information sections and what is missing.",
      "sees": [
        "Legal identity",
        "Birth and Social Security",
        "Marital status",
        "Address",
        "Occupation",
        "Race and ethnicity",
        "Education",
        "Military service",
        "Parents",
        "Family representative"
      ],
      "actions": [
        "Open section",
        "Send to another family member",
        "Save progress"
      ],
      "problemSolved": "Replaces a separate online form with death certificate information stored with the case.",
      "layout": {
        "header": "Death Certificate Information",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Legal Identity",
          "Birth and Social Security",
          "Marital Status",
          "Address",
          "Occupation",
          "Military Service"
        ],
        "statusChips": [
          "Complete",
          "Needs Review",
          "Missing"
        ],
        "actions": [
          "Open Section",
          "Send Link"
        ]
      },
      "route": "/workspace/vital-statistics-overview",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-statistics-overview.png",
      "sequence": 7,
      "acceptanceCriteria": [
        "Vital Statistics Wizard Overview renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Legal identity.",
        "The primary action \"Open section\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-legal-identity",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Legal Identity",
      "audience": "Family representative and staff",
      "purpose": "Captures the loved one's official legal name.",
      "sees": [
        "Full legal name",
        "Name copied from case if available",
        "Review status"
      ],
      "actions": [
        "Save identity",
        "Copy from loved one details",
        "Mark needs staff review"
      ],
      "problemSolved": "Staff can use one official name across paperwork.",
      "layout": {
        "header": "Legal Identity",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Full legal name"
        ],
        "cards": [
          "Copied from case",
          "Review status"
        ],
        "actions": [
          "Save",
          "Needs Staff Review"
        ]
      },
      "route": "/workspace/vital-legal-identity",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-legal-identity.png",
      "sequence": 8,
      "acceptanceCriteria": [
        "Vital Statistics: Legal Identity renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Full legal name.",
        "The primary action \"Save identity\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-birth-social-security",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Birth and Social Security",
      "audience": "Family representative and authorized staff",
      "purpose": "Collects birth date, Social Security number, and place of birth.",
      "sees": [
        "Date of birth",
        "Secure Social Security field",
        "City and state or country of birth",
        "Privacy note"
      ],
      "actions": [
        "Save",
        "Show or hide Social Security number",
        "Mark unknown for staff"
      ],
      "problemSolved": "Sensitive information is collected in one secure place and not passed around by email.",
      "layout": {
        "header": "Birth and Social Security",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Date of birth",
          "Social Security number",
          "Birth city, state, or country"
        ],
        "notes": [
          "Restricted visibility"
        ],
        "actions": [
          "Save",
          "Hide Number"
        ]
      },
      "route": "/workspace/vital-birth-social-security",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-birth-social-security.png",
      "sequence": 9,
      "acceptanceCriteria": [
        "Vital Statistics: Birth and Social Security renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Date of birth.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-marital-status",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Marital Status",
      "audience": "Family representative and staff",
      "purpose": "Captures marital status and spouse name when needed.",
      "sees": [
        "Married",
        "Married, but separated",
        "Widowed",
        "Divorced",
        "Never Married",
        "Spouse name if married"
      ],
      "actions": [
        "Choose status",
        "Add spouse name",
        "Save"
      ],
      "problemSolved": "The death certificate information stays complete and easy to review.",
      "layout": {
        "header": "Marital Status",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Marital status",
          "Spouse name if needed"
        ],
        "actions": [
          "Save and Continue"
        ]
      },
      "route": "/workspace/vital-marital-status",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-marital-status.png",
      "sequence": 10,
      "acceptanceCriteria": [
        "Vital Statistics: Marital Status renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Married.",
        "The primary action \"Choose status\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-legal-residence",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Legal Residence",
      "audience": "Family representative and staff",
      "purpose": "Collects the loved one's legal home address.",
      "sees": [
        "Street address",
        "City",
        "State",
        "Zip code",
        "Copy from case option"
      ],
      "actions": [
        "Save",
        "Copy from loved one details",
        "Mark for staff review"
      ],
      "problemSolved": "Staff does not have to retype address information into another worksheet.",
      "layout": {
        "header": "Legal Residence",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Street address",
          "City",
          "State",
          "Zip code"
        ],
        "actions": [
          "Save",
          "Copy Address"
        ]
      },
      "route": "/workspace/vital-legal-residence",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-legal-residence.png",
      "sequence": 11,
      "acceptanceCriteria": [
        "Vital Statistics: Legal Residence renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Street address.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-occupation-industry",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Occupation and Industry",
      "audience": "Family representative and staff",
      "purpose": "Captures occupation and industry in the wording needed for paperwork.",
      "sees": [
        "Occupation",
        "Industry",
        "Reminder that retired alone cannot be used",
        "Wording suggestion panel"
      ],
      "actions": [
        "Save",
        "Accept wording suggestion",
        "Ask staff for help"
      ],
      "problemSolved": "Karen can catch wording issues before paperwork is prepared.",
      "layout": {
        "header": "Occupation and Industry",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Occupation",
          "Industry"
        ],
        "notes": [
          "Cannot use retired by itself"
        ],
        "cards": [
          "Suggested wording"
        ],
        "actions": [
          "Save",
          "Use Suggestion"
        ]
      },
      "route": "/workspace/vital-occupation-industry",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-occupation-industry.png",
      "sequence": 12,
      "acceptanceCriteria": [
        "Vital Statistics: Occupation and Industry renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Occupation.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-race-ethnicity",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Race and Ethnicity",
      "audience": "Family representative and staff",
      "purpose": "Collects race and ethnicity fields from the current death certificate information form.",
      "sees": [
        "Race choices",
        "Tribe or other race detail when needed",
        "Haitian or Hispanic ethnicity choices"
      ],
      "actions": [
        "Select choices",
        "Add detail",
        "Save"
      ],
      "problemSolved": "The current form fields are captured directly in the case.",
      "layout": {
        "header": "Race and Ethnicity",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Race choices",
          "Additional detail",
          "Ethnicity choices"
        ],
        "actions": [
          "Save and Continue"
        ]
      },
      "route": "/workspace/vital-race-ethnicity",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-race-ethnicity.png",
      "sequence": 13,
      "acceptanceCriteria": [
        "Vital Statistics: Race and Ethnicity renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Race choices.",
        "The primary action \"Select choices\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-education",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Education",
      "audience": "Family representative and staff",
      "purpose": "Captures highest education level.",
      "sees": [
        "8th grade or less",
        "High school options",
        "College options",
        "Associate, bachelors, masters, doctorate"
      ],
      "actions": [
        "Choose education level",
        "Save"
      ],
      "problemSolved": "One required death certificate question is completed without a separate form.",
      "layout": {
        "header": "Education",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Highest education level"
        ],
        "actions": [
          "Save and Continue"
        ]
      },
      "route": "/workspace/vital-education",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-education.png",
      "sequence": 14,
      "acceptanceCriteria": [
        "Vital Statistics: Education renders with the Monarch-branded shell and screen title visible.",
        "The screen shows 8th grade or less.",
        "The primary action \"Choose education level\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-military-service",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Military Service",
      "audience": "Family representative and staff",
      "purpose": "Records whether the loved one served in the US Military.",
      "sees": [
        "Yes or no military service",
        "VA discharge or DD-214 request if yes",
        "Upload option"
      ],
      "actions": [
        "Save",
        "Upload DD-214",
        "Send upload request"
      ],
      "problemSolved": "If military service is selected, the needed document is placed on the checklist automatically.",
      "layout": {
        "header": "Military Service",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Military service",
          "VA / DD-214 document"
        ],
        "actions": [
          "Save",
          "Upload Document",
          "Send Request"
        ]
      },
      "route": "/workspace/vital-military-service",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-military-service.png",
      "sequence": 15,
      "acceptanceCriteria": [
        "Vital Statistics: Military Service renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Yes or no military service.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-parents",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Parents",
      "audience": "Family representative and staff",
      "purpose": "Captures parent names for death certificate information.",
      "sees": [
        "Father's first and last name",
        "Mother's first and maiden name"
      ],
      "actions": [
        "Save",
        "Mark unknown for staff review"
      ],
      "problemSolved": "Parent information stays with the case and can be reviewed by staff.",
      "layout": {
        "header": "Parent Information",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Father's name",
          "Mother's first and maiden name"
        ],
        "actions": [
          "Save and Continue"
        ]
      },
      "route": "/workspace/vital-parents",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-parents.png",
      "sequence": 16,
      "acceptanceCriteria": [
        "Vital Statistics: Parents renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Father's first and last name.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-informant-details",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Vital Statistics: Family Representative Details",
      "audience": "Family representative and staff",
      "purpose": "Captures the official family representative information used for the death certificate worksheet.",
      "sees": [
        "Full legal name",
        "Relationship",
        "Address",
        "Phone",
        "Email"
      ],
      "actions": [
        "Save",
        "Copy from planner information",
        "Send to another family member"
      ],
      "problemSolved": "Staff can see who provided the official information.",
      "layout": {
        "header": "Family Representative Details",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Full legal name",
          "Relationship",
          "Address",
          "Phone",
          "Email"
        ],
        "actions": [
          "Save",
          "Copy from Planner"
        ]
      },
      "route": "/workspace/vital-informant-details",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-informant-details.png",
      "sequence": 17,
      "acceptanceCriteria": [
        "Vital Statistics: Family Representative Details renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Full legal name.",
        "The primary action \"Save\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "disposition-selection",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Disposition Selection",
      "audience": "Family and staff",
      "purpose": "Captures whether the family is considering cremation, burial, entombment, shipping, or needs guidance.",
      "sees": [
        "Cremation",
        "Burial",
        "Entombment",
        "Shipping",
        "Undecided",
        "Not sure, guide me"
      ],
      "actions": [
        "Choose option",
        "Ask Monarch to guide me",
        "Save"
      ],
      "problemSolved": "The system shows the right next questions and paperwork based on the family's choice.",
      "layout": {
        "header": "Disposition Preference",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Cremation",
          "Burial",
          "Entombment",
          "Shipping",
          "Undecided"
        ],
        "actions": [
          "Continue",
          "Guide Me"
        ]
      },
      "route": "/workspace/disposition-selection",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/disposition-selection.png",
      "sequence": 18,
      "acceptanceCriteria": [
        "Disposition Selection renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Cremation.",
        "The primary action \"Choose option\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "cremation-details",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Cremation Details",
      "audience": "Family and staff",
      "purpose": "Collects cremation-specific information before staff prepares the form.",
      "sees": [
        "Urn or container",
        "Release to family",
        "Scattering",
        "Mailing address if shipped",
        "Implanted device question",
        "Religious or custom instructions"
      ],
      "actions": [
        "Save cremation details",
        "Upload permit later",
        "Ask staff for help"
      ],
      "problemSolved": "Cremation paperwork appears when needed and missing items are clear.",
      "layout": {
        "header": "Cremation Details",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Urn or container",
          "Final disposition",
          "Shipping address if needed",
          "Implanted device",
          "Custom instructions"
        ],
        "cards": [
          "Required acknowledgements"
        ],
        "actions": [
          "Save",
          "Ask Staff"
        ]
      },
      "route": "/workspace/cremation-details",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/cremation-details.png",
      "sequence": 19,
      "acceptanceCriteria": [
        "Cremation Details renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Urn or container.",
        "The primary action \"Save cremation details\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "burial-details",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Burial Details",
      "audience": "Family and staff",
      "purpose": "Collects burial and cemetery information.",
      "sees": [
        "Cemetery",
        "Casket",
        "Outer burial container",
        "Graveside service",
        "Burial timing",
        "Cemetery paperwork"
      ],
      "actions": [
        "Save burial details",
        "Upload cemetery paperwork",
        "Ask Monarch to schedule"
      ],
      "problemSolved": "Burial-related tasks and documents are tracked early.",
      "layout": {
        "header": "Burial Details",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Cemetery",
          "Casket",
          "Outer burial container",
          "Timing"
        ],
        "cards": [
          "Cemetery paperwork"
        ],
        "actions": [
          "Save",
          "Upload Paperwork"
        ]
      },
      "route": "/workspace/burial-details",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/burial-details.png",
      "sequence": 20,
      "acceptanceCriteria": [
        "Burial Details renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Cemetery.",
        "The primary action \"Save burial details\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "service-style",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Service Style",
      "audience": "Family and staff",
      "purpose": "Helps the family describe the type of service they want.",
      "sees": [
        "Traditional",
        "Faith-based",
        "Celebration of life",
        "Simple or private",
        "Direct cremation or no service",
        "Not sure"
      ],
      "actions": [
        "Choose style",
        "Add faith or custom notes",
        "Ask staff"
      ],
      "problemSolved": "Staff receives a clearer starting point for the arrangement conversation.",
      "layout": {
        "header": "Service Style",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Traditional",
          "Faith-Based",
          "Celebration",
          "Simple",
          "Private",
          "Not Sure"
        ],
        "actions": [
          "Select",
          "Ask Staff"
        ]
      },
      "route": "/workspace/service-style",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/service-style.png",
      "sequence": 21,
      "acceptanceCriteria": [
        "Service Style renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Traditional.",
        "The primary action \"Choose style\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "event-planning",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Event Planning",
      "audience": "Family and staff",
      "purpose": "Drafts the services and gatherings connected to the case.",
      "sees": [
        "Visitation",
        "Funeral ceremony",
        "Memorial service",
        "Graveside",
        "Church service",
        "Reception or repast",
        "Livestream"
      ],
      "actions": [
        "Add event",
        "Mark date to be scheduled",
        "Ask Monarch to help schedule",
        "Save"
      ],
      "problemSolved": "Events are connected to the case and later to the memorial page.",
      "layout": {
        "header": "Events and Gatherings",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Visitation",
          "Ceremony",
          "Memorial",
          "Graveside",
          "Reception",
          "Livestream"
        ],
        "tableHeaders": [
          "Event",
          "Date",
          "Location",
          "Status"
        ],
        "rows": [
          [
            "Memorial Service",
            "To schedule",
            "Monarch",
            "Needs review"
          ],
          [
            "Reception",
            "To schedule",
            "Family choice",
            "Optional"
          ]
        ],
        "actions": [
          "Add Event",
          "Save"
        ]
      },
      "route": "/workspace/event-planning",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/event-planning.png",
      "sequence": 22,
      "acceptanceCriteria": [
        "Event Planning renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Visitation.",
        "The primary action \"Add event\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "merchandise-selection",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Merchandise Selection",
      "audience": "Family and staff",
      "purpose": "Lets the family select or save items for staff review.",
      "sees": [
        "Caskets",
        "Urns",
        "Outer burial containers",
        "Register books",
        "Service folders",
        "Memorial cards",
        "Keepsakes",
        "Flowers and gifts",
        "Video tribute",
        "Live webcast"
      ],
      "actions": [
        "Select item",
        "Compare",
        "Save for later",
        "Ask staff"
      ],
      "problemSolved": "Staff can build the contract from selected items while still reviewing availability and pricing.",
      "layout": {
        "header": "Merchandise and Service Options",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Caskets",
          "Urns",
          "Service Folders",
          "Memorial Cards",
          "Keepsakes",
          "Flowers"
        ],
        "actions": [
          "Select",
          "Compare",
          "Ask Staff"
        ]
      },
      "route": "/workspace/merchandise-selection",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/merchandise-selection.png",
      "sequence": 23,
      "acceptanceCriteria": [
        "Merchandise Selection renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Caskets.",
        "The primary action \"Select item\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "digital-service-folder-setup",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Digital Service Folder Setup",
      "audience": "Family and staff",
      "purpose": "Collects information for a QR-accessible service folder.",
      "sees": [
        "Folder style",
        "Order of service",
        "Readings",
        "Officiant",
        "Music",
        "Eulogies",
        "Pallbearers",
        "Acknowledgements",
        "Closing message",
        "QR access"
      ],
      "actions": [
        "Save folder draft",
        "Add section",
        "Preview QR page",
        "Ask staff"
      ],
      "problemSolved": "A service folder can be prepared even when printed folders are delayed.",
      "layout": {
        "header": "Digital Service Folder",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Order of service",
          "Readings",
          "Music",
          "Pallbearers",
          "Acknowledgements"
        ],
        "cards": [
          "QR Preview"
        ],
        "actions": [
          "Save Draft",
          "Preview"
        ]
      },
      "route": "/workspace/digital-service-folder-setup",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/digital-service-folder-setup.png",
      "sequence": 24,
      "acceptanceCriteria": [
        "Digital Service Folder Setup renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Folder style.",
        "The primary action \"Save folder draft\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "obituary-biography-intake",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Obituary / Biography Intake",
      "audience": "Family and staff",
      "purpose": "Collects story information for staff review.",
      "sees": [
        "Family-provided story",
        "Survivors",
        "Preceded in death",
        "Photo upload",
        "Tone preference",
        "Staff review note"
      ],
      "actions": [
        "Save story",
        "Draft with Monarch Assistant",
        "Upload photo",
        "Ask staff"
      ],
      "problemSolved": "Memorial content starts with the case instead of being copied later.",
      "layout": {
        "header": "Life Story and Obituary Notes",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "formGroups": [
          "Life story",
          "Survivors",
          "Preceded in death",
          "Tone"
        ],
        "cards": [
          "Photo upload",
          "Staff review needed"
        ],
        "actions": [
          "Save",
          "Draft With Assistant"
        ]
      },
      "route": "/workspace/obituary-biography-intake",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/obituary-biography-intake.png",
      "sequence": 25,
      "acceptanceCriteria": [
        "Obituary / Biography Intake renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Family-provided story.",
        "The primary action \"Save story\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "guestbook-memorial-preferences",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Guestbook and Memorial Preferences",
      "audience": "Family and staff",
      "purpose": "Lets the family choose guestbook and memorial page options.",
      "sees": [
        "Enable guestbook",
        "Allow photos",
        "Allow family messages",
        "Moderation preference",
        "Enable RSVP",
        "Enable service folder QR",
        "Enable keepsake ordering"
      ],
      "actions": [
        "Save preferences",
        "Preview guestbook",
        "Ask staff"
      ],
      "problemSolved": "Staff knows what should appear on the public memorial page after approval.",
      "layout": {
        "header": "Memorial Page Preferences",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Guestbook",
          "Photo Uploads",
          "Messages",
          "RSVP",
          "Service Folder QR",
          "Keepsakes"
        ],
        "actions": [
          "Save Preferences",
          "Preview"
        ]
      },
      "route": "/workspace/guestbook-memorial-preferences",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/guestbook-memorial-preferences.png",
      "sequence": 26,
      "acceptanceCriteria": [
        "Guestbook and Memorial Preferences renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Enable guestbook.",
        "The primary action \"Save preferences\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "family-document-upload",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Family Document Upload",
      "audience": "Family and staff",
      "purpose": "Lets the family upload documents connected to the case.",
      "sees": [
        "ID",
        "VA discharge or DD-214",
        "Cemetery paperwork",
        "Cremation permit",
        "Insurance documents",
        "Other files",
        "File status and notes"
      ],
      "actions": [
        "Upload document",
        "Add note",
        "Replace file",
        "Send upload link"
      ],
      "problemSolved": "Documents arrive in the case instead of separate emails and folders.",
      "layout": {
        "header": "Upload Documents",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Identification",
          "DD-214",
          "Cemetery",
          "Cremation Permit",
          "Insurance",
          "Other"
        ],
        "tableHeaders": [
          "Document",
          "Status",
          "Staff Note"
        ],
        "rows": [
          [
            "DD-214",
            "Requested",
            "Needed if military service"
          ],
          [
            "Cremation Permit",
            "Missing",
            "Staff will review"
          ]
        ],
        "actions": [
          "Upload",
          "Send Link"
        ]
      },
      "route": "/workspace/family-document-upload",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/family-document-upload.png",
      "sequence": 27,
      "acceptanceCriteria": [
        "Family Document Upload renders with the Monarch-branded shell and screen title visible.",
        "The screen shows ID.",
        "The primary action \"Upload document\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "arrangement-review",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Arrangement Review",
      "audience": "Family and staff",
      "purpose": "Shows the family what they entered before sending it to Monarch.",
      "sees": [
        "Summary of selections",
        "Missing information",
        "Estimated range",
        "Required forms preview",
        "Monarch review reminder"
      ],
      "actions": [
        "Submit to Monarch",
        "Edit section",
        "Save draft",
        "Call Monarch"
      ],
      "problemSolved": "The family can review everything once, and staff receives organized information.",
      "layout": {
        "header": "Review Before Sending to Monarch",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Family Details",
          "Death Certificate Info",
          "Disposition",
          "Service Choices",
          "Documents"
        ],
        "mainSections": [
          "Missing information",
          "Estimated range",
          "Forms to prepare"
        ],
        "actions": [
          "Submit to Monarch",
          "Edit"
        ]
      },
      "route": "/workspace/arrangement-review",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/arrangement-review.png",
      "sequence": 28,
      "acceptanceCriteria": [
        "Arrangement Review renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Summary of selections.",
        "The primary action \"Submit to Monarch\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "family-confirmation",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Family Confirmation",
      "audience": "Family",
      "purpose": "Confirms that Monarch received the arrangement information.",
      "sees": [
        "Submission received message",
        "Next steps",
        "Assigned staff placeholder",
        "Documents and signatures pending",
        "Call Monarch button",
        "Return link"
      ],
      "actions": [
        "Open family portal",
        "Call Monarch",
        "Upload more documents"
      ],
      "problemSolved": "Families know what happens next instead of calling to confirm receipt.",
      "layout": {
        "header": "Monarch Received Your Information",
        "leftNav": [
          "Start",
          "Family Info",
          "Loved One",
          "Death Certificate Info",
          "Choices",
          "Review"
        ],
        "cards": [
          "Next Steps",
          "Documents Pending",
          "Signature Packet Pending"
        ],
        "actions": [
          "Open Family Portal",
          "Call Monarch"
        ]
      },
      "route": "/arrangements/confirmation",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/family-confirmation.png",
      "sequence": 29,
      "acceptanceCriteria": [
        "Family Confirmation renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Submission received message.",
        "The primary action \"Open family portal\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "family-follow-up-portal",
      "section": "B. Family Arrangement and Intake",
      "group": "family",
      "title": "Family Follow-Up Portal",
      "audience": "Family and staff",
      "purpose": "Gives the family one place to complete missing items after submission.",
      "sees": [
        "Case status",
        "Missing information",
        "Signature packet",
        "Uploaded documents",
        "Staff messages",
        "Service details",
        "Service folder preview"
      ],
      "actions": [
        "Complete missing information",
        "Sign documents",
        "Upload documents",
        "Reply to staff"
      ],
      "problemSolved": "Families can finish tasks from one page, and staff can see what is complete.",
      "layout": {
        "header": "Family Portal",
        "leftNav": [
          "Status",
          "Tasks",
          "Signatures",
          "Documents",
          "Messages",
          "Service Details"
        ],
        "cards": [
          "Missing Information",
          "Signature Packet",
          "Document Uploads"
        ],
        "mainSections": [
          "Messages from Monarch",
          "Service folder preview"
        ],
        "actions": [
          "Complete Task",
          "Sign",
          "Upload"
        ]
      },
      "route": "/family/portal/monarch-family",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/family-follow-up-portal.png",
      "sequence": 30,
      "acceptanceCriteria": [
        "Family Follow-Up Portal renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Case status.",
        "The primary action \"Complete missing information\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "staff-login",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Staff Login",
      "audience": "Karen and Monarch staff",
      "purpose": "Lets staff enter the staff workspace securely.",
      "sees": [
        "Email sign-in",
        "Password field",
        "Secured one-click sign-in option",
        "Role-based access note"
      ],
      "actions": [
        "Sign in",
        "Send secured sign-in link",
        "Reset password"
      ],
      "problemSolved": "Only staff can reach case, paperwork, and office management screens.",
      "layout": {
        "header": "Staff Sign In",
        "leftNav": [
          "Secure Access"
        ],
        "formGroups": [
          "Email",
          "Password"
        ],
        "cards": [
          "Secured sign-in link"
        ],
        "actions": [
          "Sign In",
          "Send Link"
        ]
      },
      "route": "/workspace/staff-login",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/staff-login.png",
      "sequence": 31,
      "acceptanceCriteria": [
        "Staff Login renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Email sign-in.",
        "The primary action \"Sign in\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "staff-dashboard",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Staff Dashboard",
      "audience": "Karen and Monarch staff",
      "purpose": "Shows everything that needs attention today.",
      "sees": [
        "New online arrangements",
        "Cases needing review",
        "Forms waiting for signature",
        "Missing documents",
        "Payments pending",
        "Services this week",
        "Guestbook messages to review",
        "Keepsake orders"
      ],
      "actions": [
        "Open case",
        "Create new case",
        "Send family link",
        "Review pending signature",
        "Log a call"
      ],
      "problemSolved": "Replaces scattered notes, spreadsheets, and memory-based follow-up.",
      "layout": {
        "header": "Today at Monarch",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "New Online Arrangements",
          "Cases Needing Review",
          "Forms Waiting for Signature",
          "Missing Documents",
          "Payments Pending",
          "Services This Week"
        ],
        "mainSections": [
          "Priority cases",
          "Missing paperwork",
          "Recent family activity",
          "Suggested next steps"
        ],
        "actions": [
          "Open Case",
          "Create Case",
          "Send Family Link",
          "Log Call"
        ]
      },
      "route": "/",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/staff-dashboard.png",
      "sequence": 32,
      "acceptanceCriteria": [
        "Staff Dashboard renders with the Monarch-branded shell and screen title visible.",
        "The screen shows New online arrangements.",
        "The primary action \"Open case\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "case-list",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Case List",
      "audience": "Karen and staff",
      "purpose": "Lists every case with search and filters.",
      "sees": [
        "Case number",
        "Loved one",
        "Status",
        "Assigned staff",
        "Disposition",
        "Missing documents",
        "Payment status",
        "Signature status"
      ],
      "actions": [
        "Search",
        "Filter",
        "Open case",
        "Create case",
        "Export list"
      ],
      "problemSolved": "Karen can find any case quickly and see what needs attention.",
      "layout": {
        "header": "Cases",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Case",
          "Loved One",
          "Status",
          "Missing",
          "Balance"
        ],
        "rows": [
          [
            "M-2026-001",
            "Eleanor Martin",
            "Needs review",
            "Cremation permit",
            "$3,685"
          ],
          [
            "M-2026-002",
            "Robert James",
            "Waiting on family",
            "Signature",
            "$0"
          ]
        ],
        "actions": [
          "Open Case",
          "Create Case",
          "Export"
        ]
      },
      "route": "/workspace/case-list",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/case-list.png",
      "sequence": 33,
      "acceptanceCriteria": [
        "Case List renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Case number.",
        "The primary action \"Search\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "create-case",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Create Case",
      "audience": "Karen and staff",
      "purpose": "Lets staff start a case from a phone call or in-office conversation.",
      "sees": [
        "Immediate need",
        "Pre-planning",
        "Manual case entry",
        "Family intake link",
        "Assigned staff"
      ],
      "actions": [
        "Create case",
        "Start assisted arrangement",
        "Send family link",
        "Assign staff"
      ],
      "problemSolved": "Karen can start a case before the family completes anything online.",
      "layout": {
        "header": "Create a Case",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Immediate Need",
          "Pre-Planning",
          "Manual Entry"
        ],
        "formGroups": [
          "Family contact",
          "Loved one",
          "Assigned staff"
        ],
        "actions": [
          "Create Case",
          "Send Family Link"
        ]
      },
      "route": "/workspace/create-case",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/create-case.png",
      "sequence": 34,
      "acceptanceCriteria": [
        "Create Case renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Immediate need.",
        "The primary action \"Create case\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "case-overview",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Case Overview",
      "audience": "Karen, directors, and assigned staff",
      "purpose": "Shows the full case at a glance.",
      "sees": [
        "Loved one card",
        "Family contact card",
        "Arrangement status",
        "Disposition",
        "Financial summary",
        "Documents",
        "Signature status",
        "Checklist",
        "Memorial status",
        "Timeline"
      ],
      "actions": [
        "Review arrangement",
        "Generate forms",
        "Open contract",
        "Request missing information",
        "Log call"
      ],
      "problemSolved": "Staff can understand a case without opening several separate tools.",
      "layout": {
        "header": "Eleanor Grace Martin Case",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Loved One",
          "Family Contact",
          "Checklist",
          "Forms",
          "Documents",
          "Balance"
        ],
        "mainSections": [
          "Case summary",
          "Recent activity",
          "Suggested next steps"
        ],
        "actions": [
          "Request Missing Info",
          "Generate Forms",
          "Open Contract"
        ]
      },
      "route": "/workspace/case-overview",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/case-overview.png",
      "sequence": 35,
      "acceptanceCriteria": [
        "Case Overview renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Loved one card.",
        "The primary action \"Review arrangement\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "staff-arrangement-review",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Staff Arrangement Review",
      "audience": "Karen, director, and assigned staff",
      "purpose": "Lets staff review family selections before they become paperwork or website content.",
      "sees": [
        "Family selections",
        "Missing selections",
        "Staff edits",
        "Internal notes",
        "Contract item suggestions"
      ],
      "actions": [
        "Approve selections",
        "Edit selections",
        "Request changes from family",
        "Add staff note"
      ],
      "problemSolved": "Family answers become staff-reviewed case information, not unreviewed public content.",
      "layout": {
        "header": "Review Family Selections",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Disposition",
          "Service Style",
          "Events",
          "Merchandise",
          "Documents"
        ],
        "mainSections": [
          "Changes needed",
          "Staff notes"
        ],
        "actions": [
          "Approve",
          "Request Changes",
          "Add Note"
        ]
      },
      "route": "/workspace/case-overview",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/staff-arrangement-review.png",
      "sequence": 36,
      "acceptanceCriteria": [
        "Staff Arrangement Review renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Family selections.",
        "The primary action \"Approve selections\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "vital-statistics-review",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Vital Statistics Review",
      "audience": "Karen and authorized staff",
      "purpose": "Shows all death certificate information for staff review.",
      "sees": [
        "All vital fields",
        "Missing or invalid fields",
        "Review status",
        "Edit history",
        "Worksheet button"
      ],
      "actions": [
        "Approve information",
        "Request correction",
        "Edit with note",
        "Generate worksheet"
      ],
      "problemSolved": "Karen can see exactly what is missing before death certificate paperwork is prepared.",
      "layout": {
        "header": "Death Certificate Information Review",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Field",
          "Value",
          "Status"
        ],
        "rows": [
          [
            "Social Security",
            "Restricted",
            "Complete"
          ],
          [
            "Occupation",
            "Teacher",
            "Reviewed"
          ],
          [
            "DD-214",
            "Requested",
            "Needed"
          ]
        ],
        "actions": [
          "Approve",
          "Request Correction",
          "Generate Worksheet"
        ]
      },
      "route": "/workspace/vital-statistics-review",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/vital-statistics-review.png",
      "sequence": 37,
      "acceptanceCriteria": [
        "Vital Statistics Review renders with the Monarch-branded shell and screen title visible.",
        "The screen shows All vital fields.",
        "The primary action \"Approve information\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "call-tracking",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Call Tracking",
      "audience": "Karen and staff",
      "purpose": "Records phone calls connected to the case.",
      "sees": [
        "Incoming or outgoing call",
        "Contact",
        "Reason",
        "Notes",
        "Outcome",
        "Follow-up task",
        "Call timeline"
      ],
      "actions": [
        "Log call",
        "Create follow-up task",
        "Call contact",
        "Edit note"
      ],
      "problemSolved": "Important phone details stay with the case instead of in memory or paper notes.",
      "layout": {
        "header": "Calls and Notes",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "formGroups": [
          "Direction",
          "Contact",
          "Reason",
          "Notes",
          "Outcome"
        ],
        "mainSections": [
          "Call timeline",
          "Follow-up tasks"
        ],
        "actions": [
          "Log Call",
          "Create Task"
        ]
      },
      "route": "/workspace/call-tracking",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/call-tracking.png",
      "sequence": 38,
      "acceptanceCriteria": [
        "Call Tracking renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Incoming or outgoing call.",
        "The primary action \"Log call\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "case-checklist",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Case Checklist",
      "audience": "Karen and staff",
      "purpose": "Tracks what is complete, missing, or waiting for review.",
      "sees": [
        "Cremation checklist",
        "Burial checklist",
        "Veteran checklist",
        "Website checklist",
        "Financial checklist",
        "Document checklist",
        "Assigned tasks",
        "Due dates"
      ],
      "actions": [
        "Complete task",
        "Assign task",
        "Request from family",
        "Change due date"
      ],
      "problemSolved": "Karen can see exactly what is missing before a case moves forward.",
      "layout": {
        "header": "Case Checklist",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "statusChips": [
          "Missing",
          "Waiting on Family",
          "Ready for Review",
          "Complete"
        ],
        "tableHeaders": [
          "Task",
          "Owner",
          "Due",
          "Status"
        ],
        "rows": [
          [
            "Cremation Authorization",
            "Karen",
            "Today",
            "Missing"
          ],
          [
            "Cremation Permit",
            "Staff",
            "Today",
            "Waiting"
          ],
          [
            "Contract Review",
            "Director",
            "Tomorrow",
            "Ready"
          ]
        ],
        "actions": [
          "Assign",
          "Request from Family",
          "Mark Reviewed"
        ]
      },
      "route": "/workspace/case-checklist",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/case-checklist.png",
      "sequence": 39,
      "acceptanceCriteria": [
        "Case Checklist renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Cremation checklist.",
        "The primary action \"Complete task\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "document-library",
      "section": "C. Staff Case Management",
      "group": "staff",
      "title": "Document Library",
      "audience": "Karen and staff",
      "purpose": "Stores documents with the case.",
      "sees": [
        "Permits",
        "Authorizations",
        "Veteran documents",
        "Identification",
        "Death certificates",
        "Financial documents",
        "Contracts",
        "Insurance",
        "Internal or family-visible status"
      ],
      "actions": [
        "Upload document",
        "Preview",
        "Download",
        "Request from family",
        "Mark reviewed"
      ],
      "problemSolved": "Documents live with the case instead of in scattered folders and email attachments.",
      "layout": {
        "header": "Documents",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Permits",
          "Authorizations",
          "Veteran Docs",
          "ID",
          "Contracts",
          "Insurance"
        ],
        "tableHeaders": [
          "Document",
          "Category",
          "Visibility",
          "Status"
        ],
        "rows": [
          [
            "Cremation Permit",
            "Permit",
            "Staff",
            "Missing"
          ],
          [
            "DD-214",
            "Veteran",
            "Staff",
            "Requested"
          ]
        ],
        "actions": [
          "Upload",
          "Request",
          "Mark Reviewed"
        ]
      },
      "route": "/workspace/document-library",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/document-library.png",
      "sequence": 40,
      "acceptanceCriteria": [
        "Document Library renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Permits.",
        "The primary action \"Upload document\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "generated-forms",
      "section": "D. Forms, Signatures, Contracts, and Financials",
      "group": "paperwork",
      "title": "Generated Forms",
      "audience": "Karen and staff",
      "purpose": "Shows which forms are needed and prepares them from case information.",
      "sees": [
        "Authorization for Release",
        "Cremation Authorization",
        "Statement of Funeral Goods and Services",
        "Death Certificate Information",
        "Form status",
        "Preview"
      ],
      "actions": [
        "Generate form",
        "Preview",
        "Edit filled fields",
        "Send for signature",
        "Download"
      ],
      "problemSolved": "Forms can be prefilled from the case instead of typed again.",
      "layout": {
        "header": "Forms for This Case",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Form",
          "Status",
          "Next Step"
        ],
        "rows": [
          [
            "Authorization for Release",
            "Ready",
            "Send"
          ],
          [
            "Cremation Authorization",
            "Missing device answer",
            "Review"
          ],
          [
            "Statement of Goods and Services",
            "Ready",
            "Preview"
          ]
        ],
        "actions": [
          "Generate Form",
          "Send for Signature"
        ]
      },
      "route": "/workspace/generated-forms",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/generated-forms.png",
      "sequence": 41,
      "acceptanceCriteria": [
        "Generated Forms renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Authorization for Release.",
        "The primary action \"Generate form\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "signature-packet",
      "section": "D. Forms, Signatures, Contracts, and Financials",
      "group": "paperwork",
      "title": "Signature Packet",
      "audience": "Karen, staff, and family signers",
      "purpose": "Bundles forms into one signing request.",
      "sees": [
        "Forms in packet",
        "Family signers",
        "Staff witness",
        "Viewed status",
        "Signed status",
        "Reminders",
        "Signature history"
      ],
      "actions": [
        "Create packet",
        "Add signer",
        "Send signature packet",
        "Send reminder",
        "View history"
      ],
      "problemSolved": "Families can sign from one link instead of receiving separate requests.",
      "layout": {
        "header": "Signature Packet",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Forms Included",
          "Family Signers",
          "Staff Witness",
          "Packet Status"
        ],
        "tableHeaders": [
          "Signer",
          "Role",
          "Status"
        ],
        "rows": [
          [
            "Andrea Martin",
            "Family signer",
            "Sent"
          ],
          [
            "Monarch Staff",
            "Witness",
            "Waiting"
          ]
        ],
        "actions": [
          "Send Packet",
          "Send Reminder"
        ]
      },
      "route": "/workspace/signature-packet",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/signature-packet.png",
      "sequence": 42,
      "acceptanceCriteria": [
        "Signature Packet renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Forms in packet.",
        "The primary action \"Create packet\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "contract-builder",
      "section": "D. Forms, Signatures, Contracts, and Financials",
      "group": "paperwork",
      "title": "Contract Builder",
      "audience": "Karen, director, and staff with financial access",
      "purpose": "Builds the Statement of Funeral Goods and Services.",
      "sees": [
        "Professional services",
        "Facilities, equipment, and staff",
        "Transportation",
        "Merchandise",
        "Special charges",
        "Cash advances",
        "Discounts",
        "Payment received",
        "Balance due",
        "Purchaser and co-signer"
      ],
      "actions": [
        "Add item",
        "Edit item",
        "Generate contract",
        "Send signature packet",
        "Record payment"
      ],
      "problemSolved": "The contract can be built from selections but remains editable by staff.",
      "layout": {
        "header": "Statement of Funeral Goods and Services",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Professional Services",
          "Merchandise",
          "Cash Advances",
          "Payments"
        ],
        "tableHeaders": [
          "Item",
          "Section",
          "Amount"
        ],
        "rows": [
          [
            "Basic services",
            "Services",
            "$995"
          ],
          [
            "Cherry urn",
            "Merchandise",
            "$295"
          ],
          [
            "Payment received",
            "Payment",
            "$1,000"
          ]
        ],
        "mainSections": [
          "Total funeral home charges",
          "Total cash advances",
          "Balance due"
        ],
        "actions": [
          "Add Item",
          "Generate Contract",
          "Send for Signature"
        ]
      },
      "route": "/cases/arr-monarch-maria-hernandez/statement",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/contract-builder.png",
      "sequence": 43,
      "acceptanceCriteria": [
        "Contract Builder renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Professional services.",
        "The primary action \"Add item\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "financials",
      "section": "D. Forms, Signatures, Contracts, and Financials",
      "group": "paperwork",
      "title": "Financials",
      "audience": "Karen, Bill, and approved staff",
      "purpose": "Shows totals, payments, balance, adjustments, and export-ready records.",
      "sees": [
        "Contract total",
        "Payments",
        "Balance",
        "Adjustments",
        "Payment terms",
        "Internal notes",
        "Payment status"
      ],
      "actions": [
        "Add payment",
        "Add adjustment",
        "Download financial file",
        "Mark payment status"
      ],
      "problemSolved": "Balances and payments are visible without checking a separate sheet.",
      "layout": {
        "header": "Financial Summary",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Contract Total",
          "Payments",
          "Balance Due",
          "Payment Status"
        ],
        "tableHeaders": [
          "Date",
          "Type",
          "Amount",
          "Note"
        ],
        "rows": [
          [
            "Jul 6",
            "Payment",
            "$1,000",
            "Family deposit"
          ],
          [
            "Jul 6",
            "Balance",
            "$3,685",
            "Due after review"
          ]
        ],
        "actions": [
          "Add Payment",
          "Download File"
        ]
      },
      "route": "/workspace/financials",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/financials.png",
      "sequence": 44,
      "acceptanceCriteria": [
        "Financials renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Contract total.",
        "The primary action \"Add payment\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "website-memorial-management",
      "section": "E. Website, Memorial, Guestbook, and Keepsakes",
      "group": "memorial",
      "title": "Website / Memorial Management",
      "audience": "Karen and staff",
      "purpose": "Prepares the public memorial page after staff approval.",
      "sees": [
        "Obituary draft",
        "Page status",
        "Service events",
        "Guestbook settings",
        "Photo settings",
        "Service folder QR",
        "RSVP",
        "Livestream links",
        "Flower or gift links"
      ],
      "actions": [
        "Preview",
        "Publish memorial page",
        "Unpublish",
        "Send family preview",
        "Edit service details"
      ],
      "problemSolved": "Website updates are connected to the case instead of copied into another tool.",
      "layout": {
        "header": "Memorial Page Workspace",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Obituary",
          "Service Events",
          "Guestbook",
          "Service Folder QR",
          "Livestream",
          "Keepsakes"
        ],
        "mainSections": [
          "Public page preview",
          "Publish controls"
        ],
        "actions": [
          "Preview",
          "Publish Memorial Page"
        ]
      },
      "route": "/memorial-hub/arr-monarch-maria-hernandez",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/website-memorial-management.png",
      "sequence": 45,
      "acceptanceCriteria": [
        "Website / Memorial Management renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Obituary draft.",
        "The primary action \"Preview\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "digital-service-folder-builder",
      "section": "E. Website, Memorial, Guestbook, and Keepsakes",
      "group": "memorial",
      "title": "Digital Service Folder Builder",
      "audience": "Karen and staff",
      "purpose": "Builds the digital service folder and printable copy.",
      "sees": [
        "Cover",
        "Order of service",
        "Readings",
        "Eulogies",
        "Music",
        "Officiant",
        "Pallbearers",
        "Obituary",
        "Acknowledgements",
        "Closing",
        "Back cover",
        "QR preview"
      ],
      "actions": [
        "Save folder",
        "Preview QR",
        "Download PDF",
        "Send family preview",
        "Mark approved"
      ],
      "problemSolved": "Service folder content is organized in one place and can be shared by QR.",
      "layout": {
        "header": "Service Folder Builder",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "cards": [
          "Cover",
          "Order of Service",
          "Readings",
          "Music",
          "Pallbearers",
          "Back Cover"
        ],
        "mainSections": [
          "Folder preview",
          "QR preview"
        ],
        "actions": [
          "Save",
          "Download PDF",
          "Approve"
        ]
      },
      "route": "/workspace/digital-service-folder-builder",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/digital-service-folder-builder.png",
      "sequence": 46,
      "acceptanceCriteria": [
        "Digital Service Folder Builder renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Cover.",
        "The primary action \"Save folder\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "guestbook-moderation",
      "section": "E. Website, Memorial, Guestbook, and Keepsakes",
      "group": "memorial",
      "title": "Guestbook Moderation",
      "audience": "Karen and staff",
      "purpose": "Lets staff review guestbook messages and photos.",
      "sees": [
        "Guest messages",
        "Photos",
        "Private family messages",
        "Approval status",
        "Guest contact",
        "Consent status"
      ],
      "actions": [
        "Approve",
        "Hide",
        "Delete",
        "Reply or send thank-you",
        "View contact"
      ],
      "problemSolved": "Guestbook activity stays appropriate and connected to the case.",
      "layout": {
        "header": "Guestbook Review",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Guest",
          "Message",
          "Status"
        ],
        "rows": [
          [
            "Lisa R.",
            "Thinking of your family",
            "Pending"
          ],
          [
            "Michael T.",
            "Shared photo",
            "Approved"
          ]
        ],
        "actions": [
          "Approve",
          "Hide",
          "Send Thank-You"
        ]
      },
      "route": "/workspace/guestbook-moderation",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/guestbook-moderation.png",
      "sequence": 47,
      "acceptanceCriteria": [
        "Guestbook Moderation renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Guest messages.",
        "The primary action \"Approve\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "guest-contacts",
      "section": "E. Website, Memorial, Guestbook, and Keepsakes",
      "group": "memorial",
      "title": "Guest Contacts and Family Contact List",
      "audience": "Karen, Bill, and approved staff",
      "purpose": "Shows contacts collected from guestbook, RSVP, QR check-in, and keepsake requests.",
      "sees": [
        "Name",
        "Phone",
        "Email",
        "Relationship",
        "Consent",
        "Source",
        "Segments"
      ],
      "actions": [
        "Export contacts",
        "Create thank-you list",
        "View activity",
        "Filter by source"
      ],
      "problemSolved": "Monarch can follow up with families and guests from one contact list.",
      "layout": {
        "header": "Family and Guest Contacts",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Name",
          "Relationship",
          "Source",
          "Consent"
        ],
        "rows": [
          [
            "Lisa Ramos",
            "Friend",
            "Guestbook",
            "Thank-you"
          ],
          [
            "Andrew Cole",
            "Nephew",
            "RSVP",
            "Yes"
          ]
        ],
        "actions": [
          "Export",
          "Create Thank-You List"
        ]
      },
      "route": "/workspace/guest-contacts",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/guest-contacts.png",
      "sequence": 48,
      "acceptanceCriteria": [
        "Guest Contacts and Family Contact List renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Name.",
        "The primary action \"Export contacts\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "keepsake-orders",
      "section": "E. Website, Memorial, Guestbook, and Keepsakes",
      "group": "memorial",
      "title": "Keepsake Orders",
      "audience": "Karen and staff",
      "purpose": "Tracks requested keepsakes and production status.",
      "sees": [
        "Order list",
        "Item details",
        "Family requester",
        "Status",
        "Production notes",
        "Case link"
      ],
      "actions": [
        "Approve",
        "Deny",
        "Mark in production",
        "Mark fulfilled",
        "Add to contract"
      ],
      "problemSolved": "Keepsake requests do not get lost and can be connected to the case.",
      "layout": {
        "header": "Keepsake Orders",
        "leftNav": [
          "Dashboard",
          "Cases",
          "Calls",
          "Checklist",
          "Documents",
          "Forms",
          "Contracts",
          "Memorial Pages"
        ],
        "tableHeaders": [
          "Item",
          "Requester",
          "Status",
          "Case"
        ],
        "rows": [
          [
            "Service folder copies",
            "Andrea Martin",
            "Requested",
            "Eleanor Martin"
          ],
          [
            "Memorial cards",
            "Family",
            "In production",
            "Eleanor Martin"
          ]
        ],
        "actions": [
          "Approve",
          "Add to Contract",
          "Mark Fulfilled"
        ]
      },
      "route": "/workspace/keepsake-orders",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/keepsake-orders.png",
      "sequence": 49,
      "acceptanceCriteria": [
        "Keepsake Orders renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Order list.",
        "The primary action \"Approve\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "funeral-home-settings",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Funeral Home Settings",
      "audience": "Karen and approved office staff",
      "purpose": "Keeps Monarch's basic office information in one place.",
      "sees": [
        "Name",
        "Locations",
        "Phone",
        "Address",
        "Website",
        "Staff",
        "Default preferences"
      ],
      "actions": [
        "Save settings",
        "Add location",
        "Edit location"
      ],
      "problemSolved": "The correct Monarch information appears on pages, forms, and messages.",
      "layout": {
        "header": "Funeral Home Settings",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "formGroups": [
          "Funeral home name",
          "Locations",
          "Phone",
          "Address",
          "Website"
        ],
        "actions": [
          "Save Settings",
          "Add Location"
        ]
      },
      "route": "/workspace/funeral-home-settings",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/funeral-home-settings.png",
      "sequence": 50,
      "acceptanceCriteria": [
        "Funeral Home Settings renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Name.",
        "The primary action \"Save settings\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "branding-logo-management",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Branding and Logo Management",
      "audience": "Karen and approved office staff",
      "purpose": "Manages logo versions and brand colors.",
      "sees": [
        "Logo for website",
        "Logo for forms",
        "Logo for emails",
        "Logo for dark backgrounds",
        "Logo for light backgrounds",
        "Logo for print",
        "Brand colors",
        "Preview"
      ],
      "actions": [
        "Upload logo",
        "Assign logo use",
        "Preview",
        "Save"
      ],
      "problemSolved": "Old-logo forms can be replaced gradually with approved Monarch branding.",
      "layout": {
        "header": "Branding and Logos",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "cards": [
          "Website Logo",
          "Form Logo",
          "Email Logo",
          "Print Logo"
        ],
        "mainSections": [
          "Brand colors",
          "Preview"
        ],
        "actions": [
          "Upload Logo",
          "Save Branding"
        ]
      },
      "route": "/workspace/branding-logo-management",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/branding-logo-management.png",
      "sequence": 51,
      "acceptanceCriteria": [
        "Branding and Logo Management renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Logo for website.",
        "The primary action \"Upload logo\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "form-template-library",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Form Template Library",
      "audience": "Karen and approved form editors",
      "purpose": "Lists Monarch forms and their current status.",
      "sees": [
        "Form list",
        "Active forms",
        "Draft forms",
        "Retired forms",
        "PDF reference",
        "Field setup",
        "Version history"
      ],
      "actions": [
        "Create form",
        "Upload PDF reference",
        "Open editor",
        "Retire old form"
      ],
      "problemSolved": "Karen can update common forms first and add more forms later.",
      "layout": {
        "header": "Form Template Library",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "tableHeaders": [
          "Form",
          "Status",
          "Next Step"
        ],
        "rows": [
          [
            "Authorization for Release",
            "Active",
            "Ready"
          ],
          [
            "Cremation Authorization",
            "Draft",
            "Review wording"
          ],
          [
            "Statement of Goods",
            "Active",
            "Ready"
          ]
        ],
        "actions": [
          "Create Form",
          "Upload PDF"
        ]
      },
      "route": "/workspace/form-template-library",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/form-template-library.png",
      "sequence": 52,
      "acceptanceCriteria": [
        "Form Template Library renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Form list.",
        "The primary action \"Create form\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "form-template-editor",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Form Template Editor",
      "audience": "Karen and approved form editors",
      "purpose": "Lets staff update form wording and filled fields before publishing a new version.",
      "sees": [
        "Form wording",
        "Fields filled from case",
        "Signature fields",
        "Initial fields",
        "Show or hide rules",
        "Preview",
        "Publish new version"
      ],
      "actions": [
        "Save draft",
        "Add field",
        "Add signature",
        "Preview",
        "Publish version"
      ],
      "problemSolved": "Monarch can update wording without losing old signed copies.",
      "layout": {
        "header": "Form Template Editor",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "formGroups": [
          "Form wording",
          "Case fields",
          "Signature areas",
          "Initial areas"
        ],
        "mainSections": [
          "Preview",
          "Version note"
        ],
        "actions": [
          "Save Draft",
          "Publish Version"
        ]
      },
      "route": "/workspace/form-template-editor",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/form-template-editor.png",
      "sequence": 53,
      "acceptanceCriteria": [
        "Form Template Editor renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Form wording.",
        "The primary action \"Save draft\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "catalog-price-list-management",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Catalog / Price List Management",
      "audience": "Karen, Bill, and approved staff",
      "purpose": "Manages services, merchandise, and default charges used in contracts.",
      "sees": [
        "Services",
        "Merchandise",
        "Caskets",
        "Urns",
        "Folders",
        "Keepsakes",
        "Cash advance defaults",
        "Pricing",
        "Active or inactive",
        "Contract section"
      ],
      "actions": [
        "Add item",
        "Edit item",
        "Set active or inactive",
        "Save price list"
      ],
      "problemSolved": "Contract items can be selected consistently and reviewed by staff.",
      "layout": {
        "header": "Catalog and Price List",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "tableHeaders": [
          "Item",
          "Category",
          "Price",
          "Contract Section"
        ],
        "rows": [
          [
            "Cherry urn",
            "Urns",
            "$295",
            "Merchandise"
          ],
          [
            "Certified copies",
            "Cash advance",
            "$20",
            "Cash Advances"
          ]
        ],
        "actions": [
          "Add Item",
          "Save Price List"
        ]
      },
      "route": "/workspace/catalog-price-list-management",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/catalog-price-list-management.png",
      "sequence": 54,
      "acceptanceCriteria": [
        "Catalog / Price List Management renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Services.",
        "The primary action \"Add item\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "staff-permissions",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Staff and Permissions",
      "audience": "Karen, Bill, and approved administrators",
      "purpose": "Controls what each staff member can see and do.",
      "sees": [
        "Roles",
        "Case access",
        "Document access",
        "Financial access",
        "Office settings access"
      ],
      "actions": [
        "Invite staff",
        "Update permissions",
        "Deactivate user",
        "Assign location"
      ],
      "problemSolved": "Sensitive paperwork, documents, and financial details are limited to the right people.",
      "layout": {
        "header": "Staff and Permissions",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "tableHeaders": [
          "Staff",
          "Role",
          "Cases",
          "Financials"
        ],
        "rows": [
          [
            "Karen",
            "Manager",
            "All",
            "Yes"
          ],
          [
            "Staff Member",
            "Documents",
            "Assigned",
            "No"
          ]
        ],
        "actions": [
          "Invite Staff",
          "Update Access"
        ]
      },
      "route": "/workspace/staff-permissions",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/staff-permissions.png",
      "sequence": 55,
      "acceptanceCriteria": [
        "Staff and Permissions renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Roles.",
        "The primary action \"Invite staff\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "notification-templates",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Notification Templates",
      "audience": "Karen and approved office staff",
      "purpose": "Manages reusable family and staff messages.",
      "sees": [
        "Family intake link",
        "Missing information request",
        "Signature packet message",
        "Thank-you message",
        "Guestbook notice",
        "Document request",
        "Payment reminder"
      ],
      "actions": [
        "Edit message",
        "Preview",
        "Send test",
        "Save"
      ],
      "problemSolved": "Staff can send consistent messages without rewriting the same text every time.",
      "layout": {
        "header": "Message Templates",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "cards": [
          "Family Intake Link",
          "Missing Information",
          "Signature Packet",
          "Thank-You",
          "Document Request"
        ],
        "mainSections": [
          "Message preview",
          "Available fields"
        ],
        "actions": [
          "Edit",
          "Preview",
          "Save"
        ]
      },
      "route": "/workspace/notification-templates",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/notification-templates.png",
      "sequence": 56,
      "acceptanceCriteria": [
        "Notification Templates renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Family intake link.",
        "The primary action \"Edit message\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    },
    {
      "id": "reports-exports",
      "section": "F. Office Settings and Administration",
      "group": "office",
      "title": "Reports and Exports",
      "audience": "Karen, Bill, and approved staff",
      "purpose": "Downloads case, document, financial, and contact lists when needed.",
      "sees": [
        "Case exports",
        "Financial exports",
        "Contact exports",
        "Document checklist exports",
        "Accounting-ready file",
        "CSV downloads"
      ],
      "actions": [
        "Choose report",
        "Filter dates",
        "Download file",
        "View recent exports"
      ],
      "problemSolved": "Karen can pull office information without building a spreadsheet by hand.",
      "layout": {
        "header": "Reports and Exports",
        "leftNav": [
          "Settings",
          "Branding",
          "Forms",
          "Price List",
          "Staff",
          "Messages",
          "Reports"
        ],
        "cards": [
          "Case List",
          "Financials",
          "Contacts",
          "Document Checklist"
        ],
        "formGroups": [
          "Date range",
          "Case status"
        ],
        "actions": [
          "Download File",
          "View Recent"
        ]
      },
      "route": "/workspace/reports-exports",
      "wireframe": "../deliverables/karen_visual_preview/wireframes/reports-exports.png",
      "sequence": 57,
      "acceptanceCriteria": [
        "Reports and Exports renders with the Monarch-branded shell and screen title visible.",
        "The screen shows Case exports.",
        "The primary action \"Choose report\" is available as a native control.",
        "The screen includes the operational note explaining how this helps Monarch."
      ]
    }
  ],
  "forms": [
    {
      "name": "Authorization for Release",
      "purpose": "Allows Monarch to take the loved one into care.",
      "prefill": "Loved one's name, family contact, relationship, Monarch address and phone.",
      "family": "Confirms authority and signs.",
      "staff": "Reviews release location, readiness, and witness information.",
      "signs": "Family authorized person and Monarch team witness.",
      "storage": "Signed copy is stored in the case document library.",
      "checklist": "Completes the release authorization checklist item."
    },
    {
      "name": "Cremation Authorization and Disposition",
      "purpose": "Records cremation permission, final disposition, device information, and required acknowledgements.",
      "prefill": "Loved one's name, date of passing, cremation selections, urn or container, and family signer details.",
      "family": "Provides disposition choice, device answer, initials, custom instructions, and signature.",
      "staff": "Reviews completeness and countersigns where needed.",
      "signs": "Family authorizing signer and Monarch team member.",
      "storage": "Signed copy is stored with forms and documents for the case.",
      "checklist": "Completes cremation authorization and related cremation paperwork tasks."
    },
    {
      "name": "Statement of Funeral Goods and Services",
      "purpose": "Creates the itemized contract and financial statement.",
      "prefill": "Case number, loved one's name, selected goods and services, purchaser information, payments, and totals.",
      "family": "Reviews and signs after staff has prepared the statement.",
      "staff": "Adds or edits line items, discounts, cash advances, terms, and payments.",
      "signs": "Purchaser, any co-signer, and Monarch acceptance.",
      "storage": "Signed copy is stored in the case document library and linked to financials.",
      "checklist": "Completes contract prepared, sent, and signed tasks."
    },
    {
      "name": "Receipt of Death Certificates/Cremated Remains",
      "purpose": "Records the final handoff of death certificates or cremated remains to an authorized recipient.",
      "prefill": "Loved one's name, Monarch team member, certificate counts, urn description, and recipient details when available.",
      "family": "Confirms authority to receive the documents or cremated remains and signs at pickup or release.",
      "staff": "Reviews the recipient relationship, certificate counts, urn description, and custodial-care release before closing the task.",
      "signs": "Authorized recipient, with an optional second authorized-person signature line.",
      "storage": "Signed receipt is stored in the case document library with the final handoff record.",
      "checklist": "Completes death certificate receipt, cremated-remains release, and final custody handoff tasks."
    },
    {
      "name": "Vital Statistical Information",
      "purpose": "Collects death certificate information now handled through a separate online form.",
      "prefill": "Loved one's name and family contact when already entered in the case.",
      "family": "Provides legal identity, Social Security number, birthplace, marital status, address, occupation, race, education, military service, parents, and family representative details.",
      "staff": "Reviews missing or inconsistent information before preparing paperwork.",
      "signs": "No signature on the intake itself.",
      "storage": "Information stays in the case and can be used for worksheets and forms.",
      "checklist": "Completes death certificate information submitted and review tasks."
    }
  ],
  "dailyExamples": [
    "Karen receives a call and starts a case while speaking with the family.",
    "A family completes death certificate information online instead of using a separate form link.",
    "A cremation case automatically shows the cremation authorization and related document tasks.",
    "A family signs documents from one link.",
    "Staff records the receipt of death certificates or cremated remains before closing the final handoff.",
    "Karen opens the checklist and sees exactly what paperwork is missing.",
    "Staff builds the contract from selected items and can still edit the final statement.",
    "A memorial page and digital service folder are prepared after staff approval.",
    "Guestbook contacts flow into the family and guest contact list for thank-you follow-up."
  ],
  "closing": [
    "One place for every case",
    "One place for forms and signatures",
    "One place for documents",
    "One place for contracts and balances",
    "One place for website and memorial content",
    "One place for guestbook and family contacts",
    "Less retyping",
    "Fewer missing documents",
    "Clearer staff follow-up",
    "A better family experience"
  ],
  "moduleCriteria": [
    {
      "id": "entire-demo",
      "title": "Entire Monarch operating-system preview",
      "criteria": [
        "A family can start from Monarch's website, enter the arrangement flow, and reach confirmation.",
        "Staff can open the dashboard, case overview, checklist, documents, forms, signatures, contract, memorial, guestbook, CRM, and settings screens.",
        "Every Karen visual-planning page has a matching wireframe image and a matching app preview page.",
        "The design uses the approved Monarch concept direction: dark green, gold accents, serif display headings, calm white surfaces, QR/service-folder moments, and administrative density where appropriate.",
        "No critical workflow is represented only as a document; each module has an in-app screen with visible actions."
      ]
    },
    {
      "id": "family-arrangement",
      "title": "Family arrangement flow",
      "criteria": [
        "The family path begins from Monarch's website or a private staff link, not from an obituary page.",
        "The flow covers family representative, loved-one details, death certificate information, disposition, service choices, merchandise, memorial preferences, document upload, review, and confirmation.",
        "Monarch review language is visible before submission.",
        "Cremation and burial paths expose different paperwork and missing-item needs."
      ]
    },
    {
      "id": "staff-case-management",
      "title": "Staff case management",
      "criteria": [
        "The dashboard surfaces new online arrangements, cases needing review, missing documents, forms waiting for signature, services this week, payments, and suggested next steps.",
        "The case overview combines decedent, family contact, arrangement status, checklist, forms, documents, balance, activity, and AI summary.",
        "Staff can navigate from the case to checklist, calls, documents, forms, signature packet, contract, memorial, guestbook, CRM, and keepsakes."
      ]
    },
    {
      "id": "paperwork-contracts",
      "title": "Forms, signatures, contracts, and financials",
      "criteria": [
        "Release authorization, cremation authorization, receipt of death certificates or cremated remains, statement of goods and services, and vital statistics workflows are represented.",
        "Generated forms show status, next step, preview, edit mapped fields, send for signature, and download actions.",
        "Signature packet shows forms, signers, staff witness, reminders, and audit history.",
        "Contract builder shows service charges, merchandise, cash advances, payments, totals, balance due, purchaser/co-signer, and signature actions."
      ]
    },
    {
      "id": "website-crm",
      "title": "Website, memorial, guestbook, CRM, and keepsakes",
      "criteria": [
        "Staff can prepare a memorial page only after case information is approved.",
        "Digital service folder builder includes cover, order of service, readings, music, officiant, pallbearers, acknowledgements, closing, QR preview, and PDF export.",
        "Guestbook moderation supports approve, hide, delete, reply/thank-you, contact view, and consent status.",
        "Guest contacts and keepsake orders connect back to the case and contract."
      ]
    },
    {
      "id": "settings-admin",
      "title": "Settings and administration",
      "criteria": [
        "Funeral home settings, branding/logo management, form templates, catalog/pricing, staff permissions, notification templates, and reports/exports are visible.",
        "Logo management supports dark, light, print, website, form, and email use cases.",
        "Catalog items map to contract sections and active/inactive status.",
        "Reports expose case, financial, contact, document checklist, accounting-ready, and CSV exports."
      ]
    }
  ]
} as const;

export const weHonorPlanPages = weHonorSystemPlan.pages satisfies readonly WeHonorPlanPage[];

export const weHonorModuleCriteria =
  weHonorSystemPlan.moduleCriteria satisfies readonly WeHonorModuleAcceptance[];

export function getWeHonorPlanPage(id: string) {
  return weHonorPlanPages.find((page) => page.id === id);
}

export function getWeHonorPagesByGroup(group: WeHonorPlanGroup) {
  return weHonorPlanPages.filter((page) => page.group === group);
}

export function getWeHonorPagesBySection(section: string) {
  return weHonorPlanPages.filter((page) => page.section === section);
}

export function getWeHonorPlanSections() {
  return Array.from(new Set(weHonorPlanPages.map((page) => page.section)));
}
